import { useRouter } from 'next/router';

import { ChainAddress, EthereumAddress, isEthereumAddress } from '@/utils/models';
import { TokenInfo } from '@/utils/token.types';
import { getTokenInfoFromQueryParameter } from '@/utils/url';

/**
 * Get the selected tokens from the query parameter
 * If activeToken is defined, only one query parameter is required.
 */
export const useSelectedTokens = (
  activeToken?: TokenInfo,
): {
  buyTokenChainId: number | undefined;
  buyTokenAddress: ChainAddress | undefined;
  sellTokenChainId: number | undefined;
  sellTokenAddress: ChainAddress | undefined;
} => {
  const router = useRouter();

  if (!router.isReady) {
    return {
      buyTokenAddress: undefined,
      buyTokenChainId: undefined,
      sellTokenChainId: undefined,
      sellTokenAddress: undefined,
    };
  }

  const {
    query: {
      buyAddress: buyAddressQueryParam,
      buyChain: buyChainQueryParam,
      sellChain: sellChainQueryParam,
      sellAddress: sellAddressQueryParam,
    },
  } = router;

  const buyToken = getTokenInfoFromQueryParameter(buyAddressQueryParam, buyChainQueryParam);
  const sellToken = getTokenInfoFromQueryParameter(sellAddressQueryParam, sellChainQueryParam);

  /** Conditionally determine the buy and sell tokens based on either the query parameter or active token (from the path) */
  if (!buyToken && !sellToken) {
    // if no query parameters are present,
    // default to setting the buy token from the path
    return {
      buyTokenChainId: activeToken?.chainId,
      buyTokenAddress: activeToken?.address,
      sellTokenChainId: undefined,
      sellTokenAddress: undefined,
    };
  } else if (
    buyToken &&
    !sellToken &&
    buyToken.address === activeToken?.address &&
    buyToken.chainId === activeToken?.chainId
  ) {
    // if a buy token query parameter exists but there is no sell token,
    // set the buy token and leave the sell token undefined
    return {
      buyTokenChainId: activeToken?.chainId,
      buyTokenAddress: activeToken?.address,
      sellTokenChainId: undefined,
      sellTokenAddress: undefined,
    };
  } else if (
    !buyToken &&
    sellToken &&
    sellToken.address === activeToken?.address &&
    sellToken.chainId === activeToken?.chainId
  ) {
    // If a sell token parameter exists but there is no buy token,
    // set the sell token and leave the buy token undefined.
    return {
      buyTokenChainId: undefined,
      buyTokenAddress: undefined,
      sellTokenChainId: activeToken?.chainId,
      sellTokenAddress: activeToken?.address,
    };
  } else {
    // default case
    // Use the buy or sell query parameters or fallback to the active token.
    return {
      buyTokenChainId: buyToken ? buyToken.chainId : activeToken?.chainId,
      buyTokenAddress: buyToken
        ? isEthereumAddress(buyToken.address)
          ? (buyToken.address.toLowerCase() as EthereumAddress)
          : buyToken.address
        : activeToken?.address,
      sellTokenChainId: sellToken ? sellToken.chainId : activeToken?.chainId,
      sellTokenAddress: sellToken
        ? isEthereumAddress(sellToken.address)
          ? (sellToken.address.toLowerCase() as EthereumAddress)
          : sellToken.address
        : activeToken?.address,
    };
  }
};
